/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  deleteDocument,
  getDocumentFile,
  loadDocuments,
} from "../../../../services/documentsManager";
import type { IFinancialEntry } from "../../../../types/financial";
import Button from "../../../../components/Button/Button";
import styles from "./ExpensesAdd.module.css";

import { ArrowBack, Edit } from "@mui/icons-material";

import DocumentHistoryModal from "../../../../components/DocumentsManager/DocumentHistoryModal";
import DocumentSettingsModal from "../../../../components/DocumentsManager/DocumentSettingsModal";
import DocumentsList from "../../../../components/DocumentsManager/DocumentsList";
import DocumentUploadModal from "../../../../components/DocumentsManager/DocumentUploadModal";
import {
  handleDownloadDocument,
  handleViewDocument,
} from "../../../../components/DocumentsManager/DocumentUtils";
import FormExpenses from "./FormExpenses";

const ExpensesAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { uid } = useParams();
  const queryClient = useQueryClient();

  const entidade = "FINANCPAG";

  const stateEntryToEdit = location.state?.entryToEdit as
    | IFinancialEntry
    | undefined;
  const stateEntryToCopy = location.state?.entryToCopy as
    | IFinancialEntry
    | undefined;

  const [isModalUploadOpen, setIsModalUploadOpen] = useState(false);
  const [isModalConfigOpen, setIsModalConfigOpen] = useState(false);

  const [isModalHistoryOpen, setIsModalHistoryOpen] = useState(false);
  const [historyDocType, setHistoryDocType] = useState<string>("");

  const [currentUid, setCurrentUid] = useState<string | undefined>(
    uid || stateEntryToEdit?.uid_financeiro_lancamento,
  );

  const [preSelectedDocType, setPreSelectedDocType] = useState("");

  const { data: documentsData } = useQuery({
    queryKey: ["financialDocuments", currentUid],
    queryFn: () => loadDocuments(currentUid!),
    enabled: !!currentUid,
  });

  const [isVisible, setIsVisible] = useState<{
    documents?: boolean;
    isVisibleDocumentsModal?: boolean;
    isEdit?: boolean;
  }>({
    documents: false,
    isVisibleDocumentsModal: false,
    isEdit: !currentUid,
  });

  const { mutateAsync: deleteDocMutation } = useMutation({
    mutationFn: deleteDocument,
    onSuccess: (response) => {
      toast.success(response.message);
      queryClient.invalidateQueries({
        queryKey: ["financialDocuments", currentUid],
      });
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const handleView = useCallback(async (doc: any) => {
    await handleViewDocument(doc.uid_documento, getDocumentFile);
  }, []);

  const handleDownload = useCallback(async (doc: any) => {
    await handleDownloadDocument(doc.uid_documento, getDocumentFile);
  }, []);

  const handleDeleteDocument = useCallback(
    async (doc: any) => {
      const confirm = window.confirm(
        `Deseja realmente excluir o documento "${doc.titulo}"?`,
      );

      if (confirm) {
        try {
          await deleteDocMutation(doc.uid_documento);
        } catch (error) {
          console.error(error);
        }
      }
    },
    [deleteDocMutation],
  );

  const handleUploadVersion = useCallback((doc: any) => {
    if (doc.uid_tipo_documento) {
      setPreSelectedDocType(doc.uid_tipo_documento);
      setIsModalUploadOpen(true);
    } else {
      setPreSelectedDocType("");
      setIsModalUploadOpen(true);
    }
  }, []);

  const handleHistory = useCallback((doc: any) => {
    if (doc.uid_tipo_documento) {
      setHistoryDocType(doc.uid_tipo_documento);
      setIsModalHistoryOpen(true);
    }
  }, []);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      window.close();
    }
  };

  const handleVisible = (fieldName: string, e: boolean) => {
    setIsVisible((prev) => ({
      ...prev,
      [fieldName]: e,
    }));
  };

  return (
    <div className={styles.containerPage}>
      {isModalUploadOpen && (
        <DocumentUploadModal
          onClose={() => {
            setIsModalUploadOpen(false);
            setPreSelectedDocType("");
          }}
          onSuccess={() => {
            setIsModalUploadOpen(false);
            setPreSelectedDocType("");
            queryClient.invalidateQueries({
              queryKey: ["financialDocuments", currentUid],
            });
          }}
          entidade={entidade}
          uid_vinculo={currentUid || ""}
          initialDocType={preSelectedDocType}
        />
      )}

      {isModalHistoryOpen && (
        <DocumentHistoryModal
          onClose={() => {
            setIsModalHistoryOpen(false);
            setHistoryDocType("");
            queryClient.invalidateQueries({
              queryKey: ["financialDocuments", currentUid],
            });
          }}
          entidade={entidade}
          uid_vinculo={currentUid || ""}
          initialDocType={historyDocType}
        />
      )}

      {isModalConfigOpen && (
        <DocumentSettingsModal
          onClose={() => setIsModalConfigOpen(false)}
          entidade={entidade}
        />
      )}

      <div className={styles.headerPage}>
        <h1>
          {currentUid ? "Editar Contas a Pagar" : "Cadastro de Contas a Pagar"}
        </h1>

        <div className={styles.headerButtons}>
          {currentUid && (
            <Button
              variant="contain"
              title="Editar lançamento"
              onClick={() => handleVisible("isEdit", !isVisible.isEdit)}
            >
              <Edit />
            </Button>
          )}
          <Button
            variant="contain"
            onClick={handleGoBack}
            title="Voltar para página anterior"
          >
            <ArrowBack />
          </Button>
        </div>
      </div>

      <div className={styles.formContainer}>
        <div className={styles.formHeader}>
          <h2>DADOS DA DESPESA</h2>
        </div>

        <div className={styles.formContent}>
          <FormExpenses
            uid={currentUid}
            isEdit={!!isVisible.isEdit}
            handleVisible={handleVisible}
            onSuccessSave={(newUid) => {
              setCurrentUid(newUid);
            }}
            entryToEdit={stateEntryToEdit}
            entryToCopy={stateEntryToCopy}
          />
        </div>

        <div
          style={{
            width: "100%",
            marginTop: "20px",
            position: "relative",
            opacity: currentUid ? 1 : 0.5,
            pointerEvents: currentUid ? "auto" : "none",
            transition: "all 0.3s ease",
          }}
        >
          {!currentUid && (
            <div
              style={{
                position: "absolute",
                top: "-15px",
                left: "0",
                fontSize: "12px",
                color: "var(--red-primary)",
                fontWeight: "bold",
              }}
            >
              * Salve os dados da despesa para habilitar o envio de documentos.
            </div>
          )}

          <DocumentsList
            data={documentsData || []}
            onAdd={() => setIsModalUploadOpen(true)}
            onSettings={() => setIsModalConfigOpen(true)}
            onView={handleView}
            onDownload={handleDownload}
            onUpload={handleUploadVersion}
            onDelete={handleDeleteDocument}
            onCopy={handleHistory}
          />
        </div>
      </div>
    </div>
  );
};

export default ExpensesAdd;
